<script setup lang="ts">
/**
 * The settings dialog: sound, the helm's feel, and how much the scenes spend
 * on looking good. Rendered once near the root and driven by
 * `state/settings-sheet`, so the harbour office and the battle's top bar open
 * the same dialog.
 *
 * It is a small game window over a scrim. Every control writes straight to
 * `state/settings`, which persists it and which the audio and the camera rig
 * already read: there is no Save. Sliders are painted grooves with a brass
 * knob (`KitSlider`), switches are painted toggles (`Check`), the quality dial
 * is three icon tiles. The one thing that does not take effect at once is
 * quality, which a scene reads when it is built; the line under it says so.
 */
import { computed, nextTick, ref, watch, type ComponentPublicInstance } from 'vue';

import { consent } from '@/consent/consent';
import { useI18n } from '@/i18n';
import {
  MAX_AIM_SENSITIVITY,
  MIN_AIM_SENSITIVITY,
  QUALITIES,
  aimSensitivity,
  audioMuted,
  clampAimSensitivity,
  invertPitch,
  masterVolume,
  musicVolume,
  sfxVolume,
  reduceMotion,
  quality,
  voiceVolume,
} from '@/state/settings';
import { closeSettings, settingsOpen } from '@/state/settings-sheet';
import { replayTour } from '@/state/tour';
import GameIcon from './kit/GameIcon.vue';
import GameWindow from './kit/GameWindow.vue';
import type { GameIconName } from './kit/icons';
import ActionButton from './panel/ActionButton.vue';
import Check from './panel/Check.vue';
import KitSlider from './panel/KitSlider.vue';
import SectionCard from './panel/SectionCard.vue';
import SheetTransition from './panel/SheetTransition.vue';
import { playCue } from '@/audio/sfx';

const { t } = useI18n();

/** The tour restarts on the server; the harbour's overlay picks it up the next time it is shown. */
const replayed = ref(false);
async function replay(): Promise<void> {
  replayed.value = await replayTour();
}
watch(settingsOpen, (open) => {
  if (!open) replayed.value = false;
});

/** The Close plate is a component; its root element is the button that takes focus. */
const closeButton = ref<ComponentPublicInstance | null>(null);
watch(settingsOpen, async (open) => {
  if (!open) return;
  await nextTick();
  const el = closeButton.value?.$el as HTMLElement | undefined;
  el?.focus?.();
});

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  event.stopPropagation();
  closeSettings();
}

/** The cookie dialog is its own window; this one steps aside so they do not stack. */
function openCookieSettings(): void {
  closeSettings();
  consent.openSettings();
}

const musicPct = computed({
  get: () => Math.round(musicVolume.value * 100),
  set: (v: number) => {
    musicVolume.value = Math.min(1, Math.max(0, v / 100));
  },
});

const sfxPct = computed({
  get: () => Math.round(sfxVolume.value * 100),
  set: (v: number) => {
    sfxVolume.value = Math.min(1, Math.max(0, v / 100));
  },
});

const voicePct = computed({
  get: () => Math.round(voiceVolume.value * 100),
  set: (v: number) => {
    voiceVolume.value = Math.min(1, Math.max(0, v / 100));
  },
});

/** The slider works in whole percent; the store keeps 0–1. */
const volumePct = computed({
  get: () => Math.round(masterVolume.value * 100),
  set: (v: number) => {
    masterVolume.value = Math.min(1, Math.max(0, v / 100));
  },
});

const sensitivity = computed({
  get: () => aimSensitivity.value,
  set: (v: number) => {
    aimSensitivity.value = clampAimSensitivity(v);
  },
});

/** The quality dial's tiles, each with its painted icon. */
const QUALITY_ICON: Readonly<Record<(typeof QUALITIES)[number], GameIconName>> = { auto: 'cog', lean: 'bolt', full: 'star' };
</script>

<template>
  <Teleport to="body">
    <SheetTransition>
      <GameWindow
        v-if="settingsOpen"
        width="46rem"
        level="dialog"
        aria-modal="true"
        data-test="settings"
        :title="t('settings.title')"
        icon="cog"
        @close="closeSettings()"
        @keydown="onKeydown"
      >
        <div class="groups">
          <SectionCard :title="t('settings.sound.title')" icon="bell">
            <label class="row">
              <span class="k-label label">{{ t('settings.sound.volume') }}</span>
              <KitSlider v-model="volumePct" data-test="settings-volume" />
              <span class="k-chip val">{{ volumePct }}%</span>
            </label>
            <label class="row">
              <span class="k-label label">{{ t('settings.sound.music') }}</span>
              <KitSlider v-model="musicPct" data-test="settings-music" />
              <span class="k-chip val">{{ musicPct }}%</span>
            </label>
            <label class="row">
              <span class="k-label label">{{ t('settings.sound.sfx') }}</span>
              <KitSlider v-model="sfxPct" data-test="settings-sfx" />
              <span class="k-chip val">{{ sfxPct }}%</span>
            </label>
            <label class="row">
              <span class="k-label label">{{ t('settings.sound.radio') }}</span>
              <KitSlider v-model="voicePct" data-test="settings-radio" />
              <span class="k-chip val">{{ voicePct }}%</span>
            </label>
            <Check v-model="audioMuted" data-test="settings-mute" @update:model-value="playCue('ui.tab')">{{ t('settings.sound.mute') }}</Check>
          </SectionCard>

          <div class="stack">
            <SectionCard :title="t('settings.helm.title')" icon="wheel">
              <label class="row">
                <span class="k-label label">{{ t('settings.helm.sensitivity') }}</span>
                <KitSlider v-model="sensitivity" :min="MIN_AIM_SENSITIVITY" :max="MAX_AIM_SENSITIVITY" :step="0.05" data-test="settings-sensitivity" />
                <span class="k-chip val">{{ sensitivity.toFixed(2) }}×</span>
              </label>
              <Check v-model="invertPitch" data-test="settings-invert">{{ t('settings.helm.invert') }}</Check>
            </SectionCard>

            <SectionCard :title="t('settings.graphics.title')" icon="binoculars">
              <div class="tiles" role="radiogroup" :aria-label="t('settings.graphics.quality')" data-test="settings-quality">
                <button
                  v-for="q in QUALITIES"
                  :key="q"
                  type="button"
                  class="k-well tile"
                  :class="{ on: quality === q }"
                  role="radio"
                  :aria-checked="quality === q"
                  :data-test="`settings-quality-${q}`"
                  @click="quality = q"
                >
                  <GameIcon :name="QUALITY_ICON[q]" :size="36" />
                  <span class="k-label">{{ t(`settings.graphics.${q}`) }}</span>
                </button>
              </div>
              <p class="note">{{ t('settings.graphics.note') }}</p>
              <Check v-model="reduceMotion" data-test="settings-reduce-motion">{{ t('settings.graphics.reduceMotion') }}</Check>
            </SectionCard>
            <SectionCard :title="t('settings.help.title')" icon="compass">
              <ActionButton size="sm" data-test="settings-replay-tour" @click="replay()">
                <GameIcon name="orders" :size="30" /> {{ t('settings.help.replayTour') }}
              </ActionButton>
              <p v-if="replayed" class="note" data-test="settings-replayed">{{ t('settings.help.replayed') }}</p>
            </SectionCard>
          </div>
        </div>
        <p class="keys">{{ t('settings.keys') }}</p>

        <template #actions>
          <ActionButton size="sm" data-test="settings-cookies" @click="openCookieSettings">
            <GameIcon name="orders" :size="30" /> {{ t('consent.settingsLink') }}
          </ActionButton>
          <ActionButton ref="closeButton" variant="primary" size="sm" data-test="settings-close" @click="closeSettings()">
            <GameIcon name="check" :size="30" /> {{ t('common.close') }}
          </ActionButton>
        </template>
      </GameWindow>
    </SheetTransition>
  </Teleport>
</template>

<style scoped>
.groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 20rem), 1fr));
  gap: 12px;
  align-items: start;
}

.stack {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.row {
  display: grid;
  grid-template-columns: minmax(5.5rem, 7.5rem) minmax(0, 1fr) 4rem;
  align-items: center;
  gap: 10px;
}

.label {
  font-size: 0.85rem;
}

.val {
  justify-content: center;
  padding-left: 0.6rem;
  font-size: 0.9rem;
  color: var(--kit-gold);
}

.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.tile {
  display: grid;
  justify-items: center;
  gap: 4px;
  min-height: 64px;
  padding: 8px 4px;
  border: 0;
  cursor: pointer;
  touch-action: manipulation;
  color: #fff;
}

.tile.on {
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.55), 0 0 0 3px var(--kit-gold);
}

.tile:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 2px;
}

.note,
.keys {
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.35;
  color: #b8c7d6;
}

.keys {
  margin-top: 10px;
  text-align: center;
}

@media (max-width: 420px) {
  .row {
    grid-template-columns: minmax(0, 1fr) 4rem;
  }

  .row .label {
    grid-column: 1 / -1;
  }
}
</style>
